import { z } from "zod";

// Customer portal: the people at a customer (a builder's purchasing desk, a
// homeowner) the office lets in. They sign in with a link emailed to them, or
// a code texted to them — no password, no Clevis account — and see that one
// customer's invoices (and pay them), estimates (and sign them), the company's
// W-9 and certificates, their jobs, and send in POs and work orders.
//
// Everything here is written by functions only. The session token never sits
// in Firestore: sessions are stored by its SHA-256.

export const PortalContactSchema = z.object({
  customerId: z.string(),
  customerName: z.string().default(""),
  name: z.string().max(120).default(""),
  /** Lowercase. Sign-in links go here. */
  email: z.string().max(200).default(""),
  /** E.164 (+15551234567) or "". Sign-in codes are texted here. */
  phone: z.string().max(20).default(""),
  status: z.enum(["active", "revoked"]).default("active"),
  invitedByUid: z.string().nullable().default(null),
  invitedByName: z.string().default(""),
  invitedAt: z.number().nullable().default(null),
  lastSignInAt: z.number().nullable().default(null),
  revokedAt: z.number().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type PortalContact = z.infer<typeof PortalContactSchema>;

export const PortalSessionSchema = z.object({
  tenantId: z.string(),
  contactId: z.string(),
  customerId: z.string(),
  via: z.enum(["invite", "email", "text"]),
  createdAt: z.number(),
  expiresAt: z.number(),
  lastUsedAt: z.number().nullable().default(null),
});
export type PortalSession = z.infer<typeof PortalSessionSchema>;

/** tenants/{t}/portalMessages/{id}: a note between a customer's portal contact and the office. */
export const PortalMessageSchema = z.object({
  customerId: z.string(),
  /** "in": the customer wrote it (contactId set); "out": the office replied. */
  direction: z.enum(["in", "out"]),
  contactId: z.string().nullable().default(null),
  contactName: z.string().default(""),
  body: z.string().min(1).max(2000),
  byUid: z.string().nullable().default(null),
  byName: z.string().default(""),
  at: z.number(),
  /** "in" messages: when the office read them (a reply marks them read). */
  readAt: z.number().nullable().default(null),
});
export type PortalMessage = z.infer<typeof PortalMessageSchema>;

/** A sign-in session lasts this long; signing in again starts a new one. */
export const PORTAL_SESSION_MS = 30 * 86_400_000;
/** A texted code is good for this long, and this many tries. */
export const PORTAL_CODE_MS = 10 * 60_000;
export const PORTAL_CODE_TRIES = 5;

/** What the portal shows a signed-in contact (built by openPortal). */
export interface PortalView {
  company: { name: string; phone: string; email: string; requestUrl: string | null; logoUrl: string | null; cardFeePct: number };
  customer: { name: string };
  contact: { name: string; email: string };
  canPayByCard: boolean;
  /** The company reads POs sent in (the intake queue is on its plan). */
  canUpload: boolean;
  invoices: Array<{
    id: string;
    number: number;
    status: string;
    sentAt: number | null;
    dueAt: number | null;
    totalCents: number;
    amountPaidCents: number;
    title: string;
    signed: boolean;
  }>;
  estimates: Array<{
    id: string;
    number: number;
    status: string;
    sentAt: number | null;
    expiresAt: number | null;
    totalCents: number;
    title: string;
    /** The page to read, sign and pay the deposit (sent estimates). */
    signUrl: string | null;
  }>;
  jobs: Array<{
    id: string;
    title: string;
    status: string;
    startAt: number | null;
    endAt: number | null;
    address: string;
    where: string;
    poNumber: string | null;
    /** Finished work they can sign off on from here (the company asks for sign-offs, not signed yet). */
    canSignOff: boolean;
    signedOffAt: number | null;
    /** This job type asks the customer to OK the visit first (the company's workflow), and whether they have. */
    asksOk: boolean;
    okAt: number | null;
    okBy: string;
  }>;
  messages: Array<{ id: string; direction: "in" | "out"; body: string; at: number; byName: string }>;
  documents: Array<{ id: string; name: string; category: string; expiresAt: number | null; fileName: string; sizeBytes: number }>;
  sent: Array<{ id: string; subject: string; at: number; status: string; result: string }>;
  /** Clevis Relay: their referral link, when the company runs the program and they're eligible. */
  referral: { link: string; shareText: string; reward: string; friendOffer: string } | null;
}
